import { useEffect } from "react";
import { BrowserRouter, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "./locales";
import { AuthProvider } from "@/features/auth/AuthContext";
import { BillingProvider } from "@/features/billing/BillingContext";
import AppRoutes from "./app/AppRoutes";
import { AppShell } from "./app/shell/AppShell";
import ProtectedRoute from "./app/ProtectedRoute";
import { useAuthGuard } from "./app/useAuthGuard";
import { useOnboardingGuard } from "./app/useOnboardingGuard";
import { AppUpdateBanner } from "./app/shell/AppUpdateBanner";
import { StagingBanner } from "./app/shell/StagingBanner";
import { useAppUpdateAvailable } from "./hooks/useAppUpdateAvailable";
import { ThemeProvider } from "./components/ui/theme-provider";
import { ToastProvider } from "./components/ui/toast";
import { TooltipProvider } from "./components/ui/tooltip";
import { ConsentProvider } from "./components/consent/ConsentProvider";
import { PageviewTracker } from "./app/PageviewTracker";

/**
 * Wrapper around BillingProvider that only auto-fetches when user is authenticated.
 * This prevents billing API calls from being made before Firebase auth is ready.
 */
export function AuthAwareBillingProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthGuard();

  return (
    <BillingProvider autoFetch={isAuthenticated} requireAuth={isAuthenticated}>
      {children}
    </BillingProvider>
  );
}

export function AppLayout() {
  const { t } = useTranslation(["common", "auth", "errors"]);
  const {
    user,
    profile,
    loading,
    isConfigured,
    accountDisabled,
    signOut,
    requiresAuth,
    pathname,
    refreshProfile,
  } = useAuthGuard();
  const { redirectTo, showNav, profileUnavailable } = useOnboardingGuard({
    user,
    profile,
    accountDisabled,
    pathname,
  });
  // App-wide: detect a new frontend build, show AppUpdateBanner, and apply
  // the update on the next route change (no-op in dev; see the hook).
  useAppUpdateAvailable(pathname);
  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[var(--background)]">
        <div className="text-[var(--muted-foreground)]">
          {t("common:labels.loading")}
        </div>
      </div>
    );
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  // Signed-in user whose profile fetch failed: surface a retry affordance
  // instead of silently falling through to the bare-div fallback below.
  if (user && profileUnavailable) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-[var(--background)] text-[var(--foreground)]">
        <div role="alert" className="text-[var(--muted-foreground)]">
          {t("errors:profile.loadFailed")}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void refreshProfile()}
            className="rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)]"
          >
            {t("common:buttons.retry")}
          </button>
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium"
          >
            {t("auth:accountPending.signOut")}
          </button>
        </div>
      </div>
    );
  }

  // Authed + onboarded users get the Calm Authority shell (sidebar + topbar).
  // Auth screens (login / onboarding / pending-approval) render bare — they
  // manage their own full-screen layouts and set showNav=false.
  if (user && showNav) {
    return (
      <AppShell
        onSignOut={signOut}
        pathname={pathname}
        profile={profile}
        user={user}
      >
        <ProtectedRoute requiresAuth={requiresAuth} isConfigured={isConfigured}>
          <AppRoutes user={user} />
        </ProtectedRoute>
      </AppShell>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[var(--background)] text-[var(--foreground)]">
      <StagingBanner />
      <AppUpdateBanner />
      <ProtectedRoute requiresAuth={requiresAuth} isConfigured={isConfigured}>
        <AppRoutes user={user} />
      </ProtectedRoute>
    </div>
  );
}

// Keeps <html lang> in sync with the active UI language (WCAG 3.1.1) —
// otherwise screen readers keep using the initial "en" pronunciation rules
// for the entire session even after the user switches to Polish.
function useSyncDocumentLanguage() {
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    const handleLanguageChanged = (lng: string) => {
      document.documentElement.lang = lng;
    };
    i18n.on("languageChanged", handleLanguageChanged);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);
}

export default function App() {
  useSyncDocumentLanguage();
  return (
    <BrowserRouter>
      <PageviewTracker />
      <ThemeProvider>
        <TooltipProvider delay={300}>
          <ToastProvider>
            <ConsentProvider>
              <AuthProvider>
                <AuthAwareBillingProvider>
                  <AppLayout />
                </AuthAwareBillingProvider>
              </AuthProvider>
            </ConsentProvider>
          </ToastProvider>
        </TooltipProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
